import React, { useCallback, useRef } from "react";
import { FileText, CheckSquare, Calendar, MapPin, Image, Waypoints } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RECORD_CONSTANTS } from "../../../constants";
import styles from "./ViewSelector.module.css";

const VIEW_CONFIG = {
  [RECORD_CONSTANTS.VIEW_TYPES.NOTE]: {
    icon: FileText,
    label: "Note",
  },
  [RECORD_CONSTANTS.VIEW_TYPES.TASK]: {
    icon: CheckSquare,
    label: "Task",
  },
  [RECORD_CONSTANTS.VIEW_TYPES.CALENDAR]: {
    icon: Calendar,
    label: "Calendar",
  },
  [RECORD_CONSTANTS.VIEW_TYPES.MAP]: {
    icon: MapPin,
    label: "Map",
  },
  [RECORD_CONSTANTS.VIEW_TYPES.GALLERY]: {
    icon: Image,
    label: "Gallery",
  },
  [RECORD_CONSTANTS.VIEW_TYPES.GRAPH]: {
    icon: Waypoints,
    label: "Graph",
  },
};

export default function ViewSelector({ activeViews, onViewClick }) {
  const { t } = useTranslation();
  const clickTimeoutRef = useRef(null);

  const handleClick = useCallback((view) => {
    // Clear any pending click timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    // Execute immediately but prevent overlapping calls
    onViewClick(view);
    
    // Prevent clicks for 100ms to avoid animation conflicts
    clickTimeoutRef.current = setTimeout(() => {
      clickTimeoutRef.current = null;
    }, 100);
  }, [onViewClick]);

  return (
    <div className={styles.container}>
      {Object.entries(VIEW_CONFIG).map(([view, config]) => {
        const Icon = config.icon;
        const viewLabel = t(`records.editor.views.labels.${view}`, config.label);
        const isMandatoryView =
          view === RECORD_CONSTANTS.MANDATORY_VIEW && activeViews.has(view);

        return (
          <button
            key={view}
            data-view={view}
            className={`${styles.toggle} ${
              activeViews.has(view) ? styles.active : ""
            } ${isMandatoryView ? styles.mandatory : ""}`}
            onClick={() => handleClick(view)}
            aria-pressed={activeViews.has(view)}
            type="button"
            title={
              isMandatoryView
                ? t(
                    "records.editor.views.note_mandatory_hint",
                    "Note view is mandatory and cannot be disabled"
                  )
                : ""
            }
            aria-label={t("records.editor.views.toggle_aria", {
              view: viewLabel,
              defaultValue: `Toggle ${viewLabel} view`,
            })}
          >
            <Icon className={styles.icon} size={20} />
            <span className={styles.label}>{viewLabel}</span>
          </button>
        );
      })}
    </div>
  );
}
